import { createMetaEventId, hasStoredMarketingConsent, getMetaEventSourceUrl } from './metaTracking';
import { openaiEventsService } from '../services/metaEvents';

type OpenAIEventData = Record<string, unknown>;

interface OaiqFunction {
  (...args: unknown[]): void;
  q?: unknown[][];
}

declare global {
  interface Window {
    oaiq?: OaiqFunction;
  }
}

const PRO_PLAN_VALUE_CENTS: Record<'monthly' | 'yearly', number> = {
  monthly: 1800,
  yearly: 18000,
};

let marketingConsentGranted = false;
let pixelInitialized = false;

const queuedEvents: Array<{
  event: string;
  data: OpenAIEventData;
  eventId: string;
}> = [];

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return undefined;
  return decodeURIComponent(match.slice(name.length + 1)) || undefined;
}

export function getOpenAIAttributionCookies(): { oppref?: string; obref?: string } {
  return {
    oppref: readCookie('__oppref'),
    obref: readCookie('__obref'),
  };
}

export const setOpenAIMarketingConsent = (granted: boolean): void => {
  marketingConsentGranted = granted;

  if (typeof window !== 'undefined' && window.oaiq) {
    window.oaiq('consent', granted);
  }

  if (!granted) {
    return;
  }
  loadOpenAIPixel();
};

export const isOpenAIPixelConfigured = (): boolean =>
  typeof window !== 'undefined' &&
  window.location.hostname === 'leadsme.app' &&
  !!process.env.REACT_APP_OPENAI_PIXEL_ID;

const canTrack = (): boolean => isOpenAIPixelConfigured() && marketingConsentGranted;

const flushOpenAIQueue = (): void => {
  if (!window.oaiq || !pixelInitialized) return;

  queuedEvents.splice(0).forEach(({ event, data, eventId }) => {
    trackOpenAIEventInternal(event, data, eventId);
  });
};

export const loadOpenAIPixel = (): void => {
  const pixelId = process.env.REACT_APP_OPENAI_PIXEL_ID;
  if (!pixelId || !canTrack() || pixelInitialized) return;

  if (document.querySelector('script[data-openai-pixel="leadsme"]')) {
    if (window.oaiq) {
      window.oaiq('consent', true);
      window.oaiq('init', { pixelId });
      pixelInitialized = true;
      flushOpenAIQueue();
    }
    return;
  }

  const oaiq: OaiqFunction = function (...args: unknown[]) {
    (oaiq.q = oaiq.q || []).push(args);
  };
  oaiq.q = [];
  window.oaiq = oaiq;

  window.oaiq('consent', false);

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  script.setAttribute('data-openai-pixel', 'leadsme');
  script.onload = () => {
    if (!window.oaiq) return;
    window.oaiq('consent', true);
    window.oaiq('init', { pixelId });
    pixelInitialized = true;
    flushOpenAIQueue();
  };
  document.head.appendChild(script);
};

function trackOpenAIEventInternal(event: string, data: OpenAIEventData, eventId: string): void {
  if (!window.oaiq || !pixelInitialized) return;
  window.oaiq('measure', event, data, { event_id: eventId });
}

export const trackOpenAIEvent = (
  event: string,
  data: OpenAIEventData,
  eventId: string = createMetaEventId(),
): string => {
  if (!canTrack()) return eventId;

  try {
    if (window.oaiq && pixelInitialized) {
      trackOpenAIEventInternal(event, data, eventId);
    } else {
      queuedEvents.push({ event, data, eventId });
      loadOpenAIPixel();
    }
  } catch {
    // ignore analytics errors
  }

  return eventId;
};

export const trackOpenAIPageView = (): void => {
  trackOpenAIEvent('page_viewed', {
    type: 'contents',
    contents: [
      {
        id: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
        name: typeof document !== 'undefined' ? document.title || 'page' : 'page',
        content_type: 'page',
      },
    ],
  });
};

export const trackOpenAICompleteRegistration = (_method: 'email' | 'google'): void => {
  const eventId = trackOpenAIEvent('registration_completed', { type: 'customer_action' });

  if (!hasStoredMarketingConsent()) {
    return;
  }

  const { oppref, obref } = getOpenAIAttributionCookies();
  void openaiEventsService.sendEvent({
    eventType: 'registration_completed',
    eventId,
    eventSourceUrl: getMetaEventSourceUrl(),
    oppref,
    obref,
    marketingConsent: true,
    data: { type: 'customer_action' },
  });
};

export const trackOpenAIInitiateCheckout = (type: 'monthly' | 'yearly'): void => {
  const planId = type === 'monthly' ? 'pro_monthly' : 'pro_yearly';
  const amount = PRO_PLAN_VALUE_CENTS[type];
  const data = {
    type: 'contents',
    amount,
    currency: 'EUR',
    contents: [
      {
        id: planId,
        name: planId,
        content_type: 'product',
        quantity: 1,
        amount,
        currency: 'EUR',
      },
    ],
  };
  const eventId = trackOpenAIEvent('checkout_started', data);

  if (!hasStoredMarketingConsent()) {
    return;
  }

  const { oppref, obref } = getOpenAIAttributionCookies();
  void openaiEventsService.sendEvent({
    eventType: 'checkout_started',
    eventId,
    eventSourceUrl: getMetaEventSourceUrl(),
    oppref,
    obref,
    marketingConsent: true,
    data,
  });
};

export const trackOpenAIPurchase = (
  type: 'monthly' | 'yearly',
  eventId: string = createMetaEventId(),
): string => {
  const planId = type === 'monthly' ? 'pro_monthly' : 'pro_yearly';
  return trackOpenAIEvent(
    'subscription_created',
    {
      type: 'plan_enrollment',
      plan_id: planId,
      amount: PRO_PLAN_VALUE_CENTS[type],
      currency: 'EUR',
    },
    eventId,
  );
};
